import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ModalContextType, ModalProviderProps, ModalInstance, ModalComponentProps } from './ModalContext.types';

/**
 * React context for managing modal state and operations.
 * Provides functions to open and close modals programmatically.
 */
const ModalContext = createContext<ModalContextType | undefined>(undefined);

/**
 * Hook to access the modal context.
 * Must be used within a ModalProvider component.
 * 
 * @returns The modal context containing openModal and closeModal functions
 * @throws Error if used outside of a ModalProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { openModal, closeModal } = useModalContext();
 *   
 *   const handleOpenModal = () => {
 *     openModal('my-modal', MyModalComponent, { title: 'Hello' });
 *   };
 *   
 *   return <button onClick={handleOpenModal}>Open Modal</button>;
 * }
 * ```
 */
export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
};

/**
 * Provider component that manages modal state and renders modal instances.
 * Wraps child components with modal context and renders all active modals.
 * 
 * @param props - The provider props
 * @param props.children - Child components that will have access to modal context
 * 
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <ModalProvider>
 *       <MyApplication />
 *     </ModalProvider>
 *   );
 * }
 * ```
 */
export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modals, setModals] = useState<Record<string, ModalInstance>>({});

  /**
   * Opens a modal with the given component and props.
   * If a modal with the same ID already exists, it will be replaced.
   */
  const openModal = useCallback(<T extends ModalComponentProps>(
    id: string,
    Component: React.ComponentType<T>,
    props: T
  ) => {
    // create a new modal instance with the given component and props. set show to true and onCancel to close the modal.
    const modalInstance: ModalInstance = {
      id,
      component: Component,
      props: {
        ...props,
        show: true,
        onCancel: () => {
          props.onCancel?.();
          closeModal(id);
        }
      }
    };

    setModals(prev => ({
      ...prev,
      [id]: modalInstance
    }));
  }, []);

  /**
   * Updates the props of an existing modal by its unique identifier.
   * If the modal doesn't exist, this operation is a no-op.
   */
  const updateModal = useCallback(<T extends ModalComponentProps>(
    id: string,
    newProps: Partial<T>
  ) => {
    setModals(prev => {
      const existingModal = prev[id];
      if (!existingModal) {
        return prev;
      }

      return {
        ...prev,
        [id]: {
          ...existingModal,
          props: {
            ...existingModal.props,
            ...newProps as ModalComponentProps,
          },
        },
      };
    });
  }, []);

  /**
   * Closes and removes a modal by its unique identifier.
   * If the modal doesn't exist, this operation is a no-op.
   */
  const closeModal = useCallback((id: string) => {
    setModals(prev => {
      const { [id]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const contextValue: ModalContextType = {
    openModal,
    closeModal,
    updateModal,
  };

  const modalComponents = useMemo(
    () => Object.values(modals).map(modal => {
      const ModalComponent = modal.component;
      return <ModalComponent key={modal.id} {...modal.props} />;
    }),
    [modals]
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modalComponents}
    </ModalContext.Provider>
  );
};